import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { RichTextEditor } from '../../components/ui/richtext';
import { ASSIGNEE_ACTOR, ticketsApi } from '../../features/tickets/api';
import { useAuth } from '../../lib/AuthContext';
import { qk } from '../../lib/queryClient';
import { useDirty } from '../../lib/dirty';
import { cn } from '../../lib/utils';
import { RequesterPicker, type RequesterValue } from '../../features/tickets/RequesterPicker';
import { EmailChipInput } from '../../features/tickets/EmailChipInput';
import {
  derivePriority,
  AGENT_PICKABLE_SOURCES,
  IMPACT_LEVELS,
  TYPE_LABEL,
  URGENCY_LEVELS,
  type Impact,
  type Urgency,
  ticketPrompt,
} from '@itsm/shared';

/**
 * Draft-first by the book: nothing leaves this modal until Create, and typed
 * content registers as dirty so navigation asks before discarding (the Global
 * Standard). Priority is DERIVED from urgency × impact via the same shared
 * function the server runs — the preview under Impact cannot disagree with
 * what gets stored. Type/Urgency/Impact are fixed small enums, so they stay
 * plain Select — the searchable Combobox is reserved for Group/Assignee,
 * real user data long enough to need search (Kai's scoping correction). The
 * description is Tiptap, same as the ticket composer.
 */
export function NewTicketPage({
  lockedType,
}: {
  /**
   * Pin the type, so `/problems?new=1` raises a PROBLEM rather than an
   * incident on a page that only lists problems. Kai, 2026-09-08: *"there's
   * no create change or problem buttons on the page"* — there was no create
   * path for either type at all, and the Create menu's entries merely
   * navigated to a list.
   */
  lockedType?: string | undefined;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [subject, setSubject] = useState('');
  const [type, setType] = useState(lockedType ?? 'incident');
  const [source, setSource] = useState('phone');
  const [urgency, setUrgency] = useState<Urgency>('medium');
  const [impact, setImpact] = useState<Impact>('medium');
  const [requester, setRequester] = useState<RequesterValue>({ email: '', name: '', isNew: false });
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { me } = useAuth();
  const nobodyGotInTouch = !!lockedType && lockedType !== 'incident' && lockedType !== 'service_request';
  useEffect(() => {
    if (!lockedType) return;
    setType(lockedType);
    // "How did they get in touch?" has no answer for a problem, a change or a
    // major incident — nobody did. `agent` is the truthful source, and the
    // control is hidden below rather than left asking an unanswerable
    // question.
    if (nobodyGotInTouch) setSource('agent');
    // The requester is deliberately NOT prefilled with the signed-in agent.
    // Kai: *"tickets shouldn't be self-requested by default"* — a ticket
    // silently attributed to whoever typed it has the wrong answer to "who is
    // waiting on this?", and nobody re-reads a field that already looks
    // filled in. The `Me` button beside the picker makes self-raising one
    // click instead of a default.
  }, [lockedType, nobodyGotInTouch]);
  // Step 43 — form templates and the ticket's Cc list.
  const [cc, setCc] = useState<string[]>([]);
  const [templateId, setTemplateId] = useState('');
  /**
   * The rest of the form.
   *
   * Kai, 2026-09-06: "everything from properties etc should be there, even
   * worknotes etc" — and he was right that a template can only ever prefill
   * what the form HAS. The form had eight fields and the reference's template
   * editor renders the whole ticket form, so the form gained the rest first.
   */
  const [statusKey, setStatusKey] = useState('');
  const [priority, setPriority] = useState('');
  // URL state on purpose: `groupId` here is an unsaved DRAFT FIELD on a create
  // form, not a filter. Putting a half-typed ticket's group in the address bar
  // would make an abandoned draft shareable and survive a refresh that is
  // meant to discard it. (The audit matches the name, and the name is the
  // same one the queue uses for a real filter.)
  const [groupId, setGroupId] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [note, setNote] = useState('');

  const groups = useQuery({ queryKey: qk.orgGroups, queryFn: ticketsApi.groups, staleTime: 5 * 60_000 });
  const statuses = useQuery({
    queryKey: qk.ticketStatuses,
    queryFn: () => ticketsApi.statuses(),
    staleTime: 5 * 60_000,
  });
  const people = useQuery({ queryKey: qk.people, queryFn: ticketsApi.people });
  /** Agents come from the chosen GROUP — assignment happens within a group,
   *  and the API refuses anything else. */
  const groupMemberIds = (groups.data?.entries ?? []).find((g) => g.id === groupId)?.memberIds ?? [];
  const agents = (people.data?.entries ?? []).filter((p) => groupMemberIds.includes(p.id));
  const templates = useQuery({
    queryKey: qk.ticketTemplates,
    queryFn: () => ticketsApi.templates(),
    staleTime: 5 * 60_000,
  });

  /**
   * Apply a template.
   *
   * It PREFILLS and then gets out of the way — every value stays editable and
   * the agent's own edits are what gets sent. Nothing is re-read from the
   * template at save time, so a template edited between picking and saving
   * cannot change the ticket under the agent's hands.
   *
   * Fields the template does not mention are left ALONE rather than reset:
   * picking a template after typing a subject must not silently delete it.
   */
  function applyTemplate(id: string) {
    setTemplateId(id);
    const t = (templates.data?.entries ?? []).find((x) => x.id === id);
    if (!t) return;
    const p = t.prefill;
    if (p.subject !== undefined) setSubject(String(p.subject));
    if (p.type !== undefined) setType(String(p.type));
    if (p.source !== undefined) setSource(String(p.source));
    if (p.urgency !== undefined) setUrgency(p.urgency as Urgency);
    if (p.impact !== undefined) setImpact(p.impact as Impact);
    if (p.bodyHtml !== undefined) setBody(String(p.bodyHtml));
    if (Array.isArray(p.cc)) setCc(p.cc as string[]);
    if (p.requesterEmail !== undefined) {
      // `@me` means the agent raising it IS the requester — a monitoring
      // incident, a backlog task. Resolved here, never stored, for the same
      // reason as the assignee sentinel.
      const want = String(p.requesterEmail);
      if (want === ASSIGNEE_ACTOR && me) {
        setRequester({ email: me.user.email, name: me.user.name ?? '', isNew: false });
      } else if (want && want !== ASSIGNEE_ACTOR) {
        setRequester({ email: want, name: '', isNew: false });
      }
    }
    if (p.statusKey !== undefined) setStatusKey(String(p.statusKey));
    if (p.priority !== undefined) setPriority(String(p.priority));
    if (p.groupId !== undefined) setGroupId(String(p.groupId ?? ''));
    // Assignee AFTER group, because the agent list depends on it.
    if (p.assigneeId !== undefined) {
      const wanted = String(p.assigneeId ?? '');
      if (wanted === ASSIGNEE_ACTOR) {
        /**
         * "Whoever creates the ticket" — resolved HERE, at the moment of use,
         * never stored. Kai: "if I'm an agent, I create a ticket using this
         * form, I want it assigned to me in the group selected."
         *
         * Only if the creator is actually in that group: the API refuses an
         * assignee who is not, and silently assigning to nobody is better
         * than a create that fails with a rule the agent did not write.
         */
        const g = (groups.data?.entries ?? []).find((x) => x.id === String(p.groupId ?? ''));
        setAssigneeId(me && g?.memberIds?.includes(me.user.id) ? me.user.id : '');
      } else {
        setAssigneeId(wanted);
      }
    }
    if (Array.isArray(p.tags)) setTags(p.tags as string[]);
    if (p.noteHtml !== undefined) setNote(String(p.noteHtml));
  }

  const hasContent =
    subject.trim().length > 0 ||
    body.replace(/<[^>]+>/g, '').trim().length > 0 ||
    requester.email.length > 0 ||
    note.replace(/<[^>]+>/g, '').trim().length > 0 ||
    cc.length > 0 ||
    tags.length > 0;
  const clearDirty = useDirty(hasContent);

  /**
   * Where Cancel goes: the list this thing would have joined.
   *
   * No confirm of its own. As a modal this asked "Discard this unsent
   * ticket?" AND then the URL change tripped the router's blocker, so you
   * were asked twice (Kai: *"there's a double discard"*). A page navigates,
   * and navigation is precisely what the one router-level guard exists to
   * intercept — which is half the reason these became pages.
   */
  const listPath = lockedType === 'problem' ? '/problems' : lockedType === 'change' ? '/changes' : '/tickets';

  async function submit() {
    setBusy(true);
    setError(null);
    const res = await ticketsApi.create({
      subject,
      type,
      source,
      urgency,
      impact,
      requesterEmail: requester.email,
      requesterName: requester.name || undefined,
      bodyHtml: body,
      ...(cc.length ? { cc } : {}),
      ...(statusKey ? { statusKey } : {}),
      ...(priority ? { priority } : {}),
      ...(groupId ? { groupId } : {}),
      ...(assigneeId ? { assigneeId } : {}),
      ...(tags.length ? { tags } : {}),
      ...(note.replace(/<[^>]+>/g, '').trim() ? { noteHtml: note } : {}),
      ...(templateId ? { templateId } : {}),
    } as never);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    await queryClient.invalidateQueries({ queryKey: qk.tickets.all });
    /**
     * Clear the guard BEFORE navigating, and reset the form with it.
     *
     * Creating a ticket navigates to it, and the modal's fields are still
     * populated at that moment — so the unsaved-changes blocker fired on the
     * one action that had just SAVED everything (Kai: "after I create a
     * ticket it says…"). Same shape as the automations editor: an action that
     * both finishes the work and moves you somewhere has to retire its own
     * dirty flag first.
     *
     * The reset is belt and braces now the form is a page that unmounts,
     * but it costs nothing and the failure it prevents — the next New ticket
     * opening pre-filled with the last one — is silent.
     */
    clearDirty();
    setSubject('');
    setBody('');
    setNote('');
    setCc([]);
    setTags([]);
    setStatusKey('');
    setPriority('');
    setGroupId('');
    setAssigneeId('');
    setTemplateId('');
    setRequester({ email: '', name: '', isNew: false });
    navigate(`/tickets/${res.body!.id}`);
  }

  const heading = lockedType ? `New ${(TYPE_LABEL[lockedType] ?? lockedType).toLowerCase()}` : 'New ticket';

  return (
    <div className="mx-auto max-w-3xl p-6">
      <div className="mb-4 flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(listPath)} aria-label="Back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-lg font-semibold">{heading}</h1>
      </div>
      <div className="space-y-3">
        {/* Step 43 — the template picker, FIRST, because picking one changes
            the fields below it and a control that rewrites the form you have
            already filled in belongs before that form, not after. Hidden
            entirely when no templates exist rather than shown empty. */}
        {(templates.data?.entries ?? []).length > 0 && (
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Start from a template
            </label>
            <Select value={templateId} onChange={(e) => applyTemplate(e.target.value)}>
              <option value="">Blank ticket</option>
              {(templates.data?.entries ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
            {templateId && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                {(templates.data?.entries ?? []).find((t) => t.id === templateId)?.description ??
                  'Everything below is still yours to change.'}
              </p>
            )}
          </div>
        )}
        {/* Requester FIRST. Kai: "Requester should be at the top of the
            form" — and the reference agrees: its new-ticket form opens on the
            requester, with "Add new requester" and "Add Cc" beside it. It is
            the right order to think in, too: WHO is asking shapes the subject
            you write and the group it belongs to, and it is the one field
            that cannot be derived from anything else here. */}
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Requester</label>
          <RequesterPicker value={requester} onChange={setRequester} />
          {requester.isNew && (
            <div className="mt-2">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Their name</label>
              <Input
                value={requester.name}
                onChange={(e) => setRequester({ ...requester, name: e.target.value })}
                placeholder="so replies aren't addressed to a bare email"
                autoFocus
              />
            </div>
          )}
        </div>
        <div>
          {/* Step 43 — the TICKET's Cc, not a message's: these people are on
              the thread from now on, and a reply defaults to them. */}
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Cc <span className="font-normal">— copied on this ticket from now on</span>
          </label>
          <EmailChipInput value={cc} onChange={setCc} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Subject</label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={ticketPrompt(type).subject}
            autoFocus
          />
        </div>
        {/* Step 43 — the rest of the ticket's properties. The template editor
            renders the whole form, so the form has to BE the whole form:
            without these, a template could prefill eight fields and no more. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Status</label>
            <Select value={statusKey} onChange={(e) => setStatusKey(e.target.value)}>
              <option value="">Open (default)</option>
              {(statuses.data?.entries ?? []).map((st) => (
                <option key={st.key} value={st.key}>
                  {st.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Group</label>
            <Select
              value={groupId}
              onChange={(e) => {
                // Changing the group empties the agent: assignment happens
                // WITHIN a group, and the API refuses a stranded assignee.
                setGroupId(e.target.value);
                setAssigneeId('');
              }}
            >
              <option value="">No group</option>
              {(groups.data?.entries ?? []).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Agent</label>
            <Select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} disabled={!groupId}>
              <option value="">{groupId ? 'Unassigned' : 'Pick a group first'}</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name ?? a.email}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Type</label>
            {lockedType ? (
              // Shown, not hidden: the type is the most important thing about
              // what is being raised, and a form that silently decides it is
              // a form you cannot check before submitting.
              <p className="flex h-9 items-center rounded-md border bg-muted/40 px-2 text-sm">
                {TYPE_LABEL[lockedType] ?? lockedType}
              </p>
            ) : (
              <Select value={type} onChange={(e) => setType(e.target.value)}>
                <option value="incident">Incident</option>
                <option value="service_request">Service request</option>
              </Select>
            )}
          </div>
          <div className={cn(nobodyGotInTouch && 'hidden')}>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              How did they get in touch?
            </label>
            <Select value={source} onChange={(e) => setSource(e.target.value)}>
              {AGENT_PICKABLE_SOURCES.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Urgency</label>
            <Select value={urgency} onChange={(e) => setUrgency(e.target.value as Urgency)}>
              {URGENCY_LEVELS.map((u) => (
                <option key={u} value={u}>
                  {u[0]!.toUpperCase() + u.slice(1)}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Impact</label>
            <Select value={impact} onChange={(e) => setImpact(e.target.value as Impact)}>
              {IMPACT_LEVELS.map((i) => (
                <option key={i} value={i}>
                  {i[0]!.toUpperCase() + i.slice(1)}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Priority from the matrix:{' '}
          <span className="font-medium text-foreground">{derivePriority(urgency, impact)}</span>
        </p>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Tags <span className="font-normal">— comma separated</span>
          </label>
          <Input
            value={tags.join(', ')}
            onChange={(e) =>
              setTags(
                e.target.value
                  .split(',')
                  .map((t) => t.trim())
                  .filter(Boolean),
              )
            }
            placeholder="broadcast, urgent"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Description</label>
          <RichTextEditor
            value={body}
            onChange={setBody}
            placeholder={ticketPrompt(type).body}
            minHeight={140}
          />
        </div>
        <div>
          {/* A private note written at creation — the runbook for a monitoring
              incident, the questions to ask on a walk-up. Tinted amber like
              every other private note in the product, because the one mistake
              that matters here is thinking the requester will see it. */}
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Internal note <span className="font-normal">— your team only, never sent</span>
          </label>
          <RichTextEditor
            value={note}
            onChange={setNote}
            placeholder="Runbook steps, what to check first…"
            tinted
            minHeight={80}
          />
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="ghost" onClick={() => navigate(listPath)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !subject.trim() || !requester.email || !body.replace(/<[^>]+>/g, '').trim()}
          >
            {busy ? 'Creating…' : `Create ${(TYPE_LABEL[type] ?? 'ticket').toLowerCase()}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
